import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import { useObserver } from 'mobx-react-lite'
import useViewContext from '@modules/order/hooks/use-view-context'
import useOrderContext from '@modules/order/hooks/use-order-context'
import { QuantityCellRef } from '../EnhancedCellTemplates/EnhancedQuantityCell/EnhancedQuantityCell'
import { find, findIndex } from 'lodash'
import {
  highlightTableColumnDoor,
  isCaptureSizeEnabled
} from '@modules/order/utils/table-column-utils'
import IADoorLevelQuantityCustomEventwrapper from './IADoorLevelQuantityCustomEventwrapper'
import {
  useEnhancedProductStatus,
  useExclusionDoor
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import classNames from 'classnames'
import { stores } from '@stores'
import { InitialAllocationUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationUIStore'
import { InitialAllocationDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationDataStore'

const useStyles = makeStyles(theme => ({
  IADoorLevelQuantitiesCellRoot: {
    width: '100%'
  },
  highlightQtyCell: {
    background: 'rgba(255, 199, 0, 0.1)'
  }
}))

// TODO: Remove this unnecessary wrapper (refactor into IADoorLevelQuantityCustomEventwrapper)
const IADoorLevelQuantitiesCell: FC<CellItem> = ({
  rowData,
  column,
  visibleColIndex,
  isActive
}) =>
  useObserver(function useHook() {
    const classes = useStyles()
    const { uiStore } = useViewContext()
    const {
      uiStore: {
        isOrderEditable,
        dataStore: { summary }
      }
    } = useOrderContext()
    const { clientOrder, client, ...rest } = summary
    const {
      getSizes,
      showProductSizes,
      navigateToNextEditableCell,
      navigateToPrevAvailableCell,
      handleIADoorLevelQuantitytyUpdate,
      updateIASizeQuantity,
      dataStore: {
        itemIndexObjectStore,
        getIAProductDataLoading,
        getIAProductData,
        isSizeQtyUpdated
      },
      activeItemStore: { setRowAndColIndex, setSizeIndex, sizeIndex }
    } = uiStore as InitialAllocationUIStore<InitialAllocationDataStore>
    const {
      productConfiguration: { featureFlags }
    } = stores

    const { field: doorId } = column
    const doorOrderId =
      find(rest?.doorOrders, doorOrder => doorOrder?.door?.id === doorId)?.id ||
      rest?.doorOrder?.doorOrderId
    const cellRef = useRef<QuantityCellRef>()
    const productRowId = rowData?.id
    const IAProductData = getIAProductData(productRowId) || {}
    const doorOrderProducts = rowData?.doorOrderProducts || {}
    const { doorOrderProducts: IAdoorOrderProducts = {} } = IAProductData
    const doorProduct = doorOrderProducts?.[doorId]
    const IADoorProduct = IAdoorOrderProducts?.[doorId]
    const isBooked = !!doorProduct
    const hasCaptureSize = isCaptureSizeEnabled(summary)
    const isExclusionDoor: boolean =
      useExclusionDoor(rowData?.excludedInDoors, rest?.doorOrders, doorOrderId) &&
      !Boolean(doorProduct)
    const isProductActive: boolean = useEnhancedProductStatus(rowData)
    const isAPICallInProgress = productRowId
      ? getIAProductDataLoading(productRowId)
      : true
    const disabled = useMemo(
      () =>
        !doorProduct ||
        !isOrderEditable ||
        isExclusionDoor ||
        !isProductActive ||
        isAPICallInProgress,
      [
        isAPICallInProgress,
        isOrderEditable,
        isProductActive,
        doorProduct,
        isExclusionDoor
      ]
    )

    useEffect(() => {
      if (!disabled && isActive) {
        if (!sizes.length) {
          setSizeIndex(-1)
        }
        if (sizeIndex === -1) {
          cellRef?.current?.focusQtyInput()
        } else {
          cellRef?.current?.focusSizeInput(sizeIndex)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellRef, isActive, disabled, sizeIndex])
    const index = productRowId ? itemIndexObjectStore.getIndexByKey(productRowId) : null
    const focus = useCallback(
      () => {
        setRowAndColIndex(index, visibleColIndex)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [index, visibleColIndex]
    )

    const navigate = useCallback(
      (toNext: boolean) => {
        if (toNext) {
          navigateToNextEditableCell(index, visibleColIndex)
        } else {
          navigateToPrevAvailableCell(index, visibleColIndex)
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [index, visibleColIndex]
    )

    const doorQuantityChangeHandler = useCallback(
      e => {
        handleIADoorLevelQuantitytyUpdate(
          productRowId,
          doorId,
          getNumberFromLocale(e.target.value),
          doorOrderId,
          hasCaptureSize
        )
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [productRowId, hasCaptureSize]
    )

    const retailClusterId = rowData?.['retailClusters-id']

    const highlightDoors = useMemo(() => {
      return highlightTableColumnDoor(
        true,
        isBooked,
        doorOrderId,
        rest?.doorOrders || [],
        retailClusterId
      )
    }, [retailClusterId, doorOrderId, rest?.doorOrders, isBooked])

    const IASizeQuantityChangeHandler = useCallback(
      (sizeId, e) => {
        updateIASizeQuantity(
          productRowId,
          doorOrderId,
          doorId,
          getNumberFromLocale(e.target.value),
          sizeId
        )
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [productRowId, doorId, IADoorProduct]
    )

    const highlightDoorCell =
      featureFlags?.highlightBookUnbookDoorDiscrepanciesInSHR && highlightDoors

    if (!rowData) return <></>

    const sizes: any =
      rowData && hasCaptureSize && showProductSizes[productRowId]
        ? getSizes(productRowId)
        : []

    return (
      <div
        className={classNames(classes.IADoorLevelQuantitiesCellRoot, {
          [classes.highlightQtyCell]: highlightDoorCell
        })}
        data-test-id='IADoorLevelQuantitiesCell'
      >
        <IADoorLevelQuantityCustomEventwrapper
          isBooked={isBooked}
          disabled={disabled} // TODO: update with actual value & update while api call
          isExclusionDoor={isExclusionDoor}
          highlightDoors={highlightDoorCell}
          doorProduct={doorProduct}
          sizesList={sizes}
          IADoorProduct={IADoorProduct}
          showProductSizes={showProductSizes[productRowId]}
          hasCaptureSize={hasCaptureSize}
          sizes={sizes}
          navigateToSize={sizeIndex => {
            focus()
            setSizeIndex(sizeIndex)
          }}
          onSizeQuantityKeyDown={() => {}}
          navigateToCell={navigate}
          onSizeQuantityFocus={sizeId => {
            focus()
            setSizeIndex(findIndex(sizes, ['id', sizeId]))
          }}
          doorLevelQuantityKeyDown={() => {}}
          onSizeQuantityMouseDown={() => {}}
          onBlurHandler={() => {
            setRowAndColIndex(-1, -1)
          }}
          onDoorQuantityChangeHandler={doorQuantityChangeHandler}
          doorLevelQuantityFocus={() => {
            focus()
            setSizeIndex(-1)
          }}
          doorLevelQuantityMouseDown={() => {}}
          showSizeQuantitySummary={
            client?.captureSize || clientOrder?.client?.captureSize
          }
          onSizeQuantityChange={IASizeQuantityChangeHandler}
          ref={cellRef}
          isSizeQtyUpdated={isSizeQtyUpdated}
        />
      </div>
    )
  })

export default IADoorLevelQuantitiesCell
